import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';

const inputStyle = {
  width: '100%', boxSizing: 'border-box', fontSize: 16, padding: '16px 18px',
  border: '1.5px solid #d1d5db', borderRadius: 10, outline: 'none',
  transition: 'border-color 0.15s', background: '#fff', color: '#0f172a',
  fontFamily: 'inherit',
};

/**
 * Shared account/property selector with typeahead + "Other" toggle.
 * Same search pattern as dispatch PropertySelector.
 *
 * Props:
 *   value        — selected account object { id, name, type } or null
 *   onChange      — callback(account | null)
 *   useOther     — boolean, whether "Other" mode is active
 *   onToggleOther — callback(bool) to switch between search/other
 *   otherValue   — string, the free-text "other" account name
 *   onOtherChange — callback(string)
 *   label         — optional label override (default: "Account Name")
 *   required      — boolean
 */
export default function AccountSelector({
  value, onChange, useOther, onToggleOther, otherValue, onOtherChange,
  label = 'Account Name', required = true,
}) {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef();
  const searchRef = useRef('');

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounced search
  const doSearch = useCallback((term) => {
    if (term.length < 1) { setOptions([]); return; }
    setLoading(true);
    api.getProperties({ search: term })
      .then(data => {
        if (searchRef.current === term) {
          setOptions(Array.isArray(data) ? data : []);
          setOpen(true);
        }
      })
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    searchRef.current = search;
    if (!search) { setOptions([]); return; }
    const timer = setTimeout(() => doSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search, doSearch]);

  function handleSelect(item) {
    onChange(item);
    setSearch('');
    setOpen(false);
  }

  function handleClear() {
    onChange(null);
    setSearch('');
    setOptions([]);
    setOpen(false);
  }

  return (
    <div>
      <label style={{
        fontSize: 14, fontWeight: 700, color: '#0f172a', display: 'block', marginBottom: 8,
      }}>
        {label} {required && <span style={{ color: '#dc2626' }}>*</span>}
      </label>
      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10, marginTop: -4 }}>
        If account is not listed, select "Other" below
      </div>

      {/* Toggle: Search / Other */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
        <button type="button"
          onClick={() => { onToggleOther(false); }}
          style={{
            padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
            border: !useOther ? '2px solid #6d28d9' : '2px solid #e2e8f0',
            background: !useOther ? '#f5f3ff' : '#fff',
            color: !useOther ? '#6d28d9' : '#64748b',
            transition: 'all 0.15s',
          }}>
          Search Accounts
        </button>
        <button type="button"
          onClick={() => { onToggleOther(true); onChange(null); }}
          style={{
            padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
            border: useOther ? '2px solid #6d28d9' : '2px solid #e2e8f0',
            background: useOther ? '#f5f3ff' : '#fff',
            color: useOther ? '#6d28d9' : '#64748b',
            transition: 'all 0.15s',
          }}>
          Other
        </button>
      </div>

      {/* Search mode */}
      {!useOther ? (
        <div ref={wrapperRef} style={{ position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Start typing to search accounts..."
              value={value ? value.name : search}
              onChange={e => {
                if (value) onChange(null);
                setSearch(e.target.value);
              }}
              onFocus={() => { if (search.length > 0 && options.length > 0) setOpen(true); }}
              style={{ ...inputStyle, paddingRight: value ? 44 : 18 }}
            />
            {value && (
              <button type="button" onClick={handleClear}
                style={{
                  position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                  background: '#f1f5f9', border: 'none', borderRadius: '50%', width: 26, height: 26,
                  fontSize: 16, color: '#64748b', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                }}>&times;</button>
            )}
            {loading && (
              <span style={{
                position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                width: 18, height: 18, border: '2px solid #e2e8f0', borderTopColor: '#6d28d9',
                borderRadius: '50%', animation: 'spin 0.6s linear infinite', display: 'inline-block',
              }} />
            )}
          </div>
          {open && (options.length > 0 || (search.length > 0 && !loading)) && (
            <ul style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
              background: '#fff', border: '1px solid #e2e8f0', borderTop: 'none',
              borderRadius: '0 0 12px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
              maxHeight: 260, overflowY: 'auto', margin: 0, padding: 0, listStyle: 'none',
            }}>
              {options.map(p => (
                <li key={p.id}
                  onClick={() => handleSelect({ ...p, type: 'property' })}
                  style={{ padding: '14px 18px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                  onMouseOver={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseOut={e => e.currentTarget.style.background = '#fff'}
                >
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{p.name}</div>
                  {p.address && <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{p.address}, {p.city}</div>}
                </li>
              ))}
              {search.length > 0 && options.length === 0 && !loading && (
                <li style={{ padding: '14px 18px', color: '#94a3b8', fontSize: 14 }}>
                  No accounts found &mdash; select "Other" above
                </li>
              )}
            </ul>
          )}
        </div>
      ) : (
        /* Other mode — free text */
        <input
          type="text"
          placeholder="Enter account or business name"
          value={otherValue}
          onChange={e => onOtherChange(e.target.value)}
          style={inputStyle}
          autoFocus
          required={required}
        />
      )}
    </div>
  );
}
